import React, { Component, Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppShell } from './components/AppShell';
import { RbacStatusProvider } from './components/admin/RbacStatus';
import { Skeleton } from './components/ui/skeleton';
import LoginPage from './pages/LoginPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import AccessDeniedPage from './pages/AccessDeniedPage';
import LauncherPage from './pages/LauncherPage';
import ComingSoonPage from './pages/ComingSoonPage';
import './App.css';

// Route-based code splitting: every workspace page is its own chunk, fetched on
// first visit (the shell stays mounted and a content skeleton shows meanwhile).
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminApprovalsPage = lazy(() => import('./pages/admin/AdminApprovalsPage'));
const AdminRolesPermissionsPage = lazy(() => import('./pages/admin/AdminRolesPermissionsPage'));
const AdminDepartmentsPage = lazy(() => import('./pages/admin/AdminDepartmentsPage'));
const AdminRegistryPage = lazy(() => import('./pages/admin/AdminRegistryPage'));
const RecycleBinPage = lazy(() => import('./pages/RecycleBinPage'));
const KanbanPage = lazy(() => import('./pages/KanbanPage'));
const LeadLogPage = lazy(() => import('./pages/LeadLogPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const IncentivesPage = lazy(() => import('./pages/IncentivesPage'));
const EmployeeDirectoryPage = lazy(() => import('./pages/EmployeeDirectoryPage'));
const EmployeeProfilePage = lazy(() => import('./pages/EmployeeProfilePage'));
const EmployeeImportPage = lazy(() => import('./pages/EmployeeImportPage'));
const MyProfilePage = lazy(() => import('./pages/MyProfilePage'));
const OrgChartPage = lazy(() => import('./pages/OrgChartPage'));
const ProjectionsPage = lazy(() => import('./pages/ProjectionsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ActivityPage = lazy(() => import('./pages/ActivityPage'));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));
const AnnouncementsPage = lazy(() => import('./pages/AnnouncementsPage'));
const MastersPage = lazy(() => import('./pages/MastersPage'));
const LeavePage = lazy(() => import('./pages/LeavePage'));
const AttendancePage = lazy(() => import('./pages/AttendancePage'));
const PoliciesPage = lazy(() => import('./pages/PoliciesPage'));
const PositionsPage = lazy(() => import('./pages/PositionsPage'));
const PolicyCentrePage = lazy(() => import('./pages/PolicyCentrePage'));
const HolidayCalendarsPage = lazy(() => import('./pages/HolidayCalendarsPage'));
const AddEmployeeWizardPage = lazy(() => import('./pages/AddEmployeeWizardPage'));
const RecruitmentPage = lazy(() => import('./pages/RecruitmentPage'));
const InductionPage = lazy(() => import('./pages/InductionPage'));
const LifecyclePage = lazy(() => import('./pages/LifecyclePage'));
const PayrollPage = lazy(() => import('./pages/PayrollPage'));
const PayrollSetupPage = lazy(() => import('./pages/PayrollSetupPage'));
const ReimbursementsPage = lazy(() => import('./pages/ReimbursementsPage'));
const ExitManagementPage = lazy(() => import('./pages/ExitManagementPage'));
const HRDashboardPage = lazy(() => import('./pages/HRDashboardPage'));
const ComplianceCalendarPage = lazy(() => import('./pages/ComplianceCalendarPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));
const QuotationsPage = lazy(() => import('./pages/QuotationsPage'));
const QuotationBuilderPage = lazy(() => import('./pages/QuotationBuilderPage'));
const QuotationSettingsPage = lazy(() => import('./pages/QuotationSettingsPage'));
const MeetingsPage = lazy(() => import('./pages/MeetingsPage'));
const MeetingSettingsPage = lazy(() => import('./pages/MeetingSettingsPage'));
const ProjectsListPage = lazy(() => import('./pages/ProjectsListPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const BillingRequestsPage = lazy(() => import('./pages/BillingRequestsPage'));
const MyTasksPage = lazy(() => import('./pages/MyTasksPage'));
const ProjectStatusSettingsPage = lazy(() => import('./pages/ProjectStatusSettingsPage'));
const ProjectTemplatesPage = lazy(() => import('./pages/ProjectTemplatesPage'));
const MyCalendarPage = lazy(() => import('./pages/MyCalendarPage'));
const PortalLoginPage = lazy(() => import('./pages/portal/PortalLoginPage'));
const PortalDashboardPage = lazy(() => import('./pages/portal/PortalDashboardPage'));
const PortalProjectPage = lazy(() => import('./pages/portal/PortalProjectPage'));

const SessionLoading = () => (
  <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground" data-testid="session-loading">
    Loading…
  </div>
);

/** Content-area placeholder while a lazily loaded page chunk downloads. */
const PageSkeleton = () => (
  <div className="space-y-5" data-testid="page-skeleton" aria-busy="true" aria-live="polite">
    <div className="space-y-2">
      <Skeleton className="h-7 w-56 rounded-md" />
      <Skeleton className="h-3.5 w-80 max-w-full rounded" />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {['a', 'b', 'c'].map((k) => <Skeleton key={k} className="h-16 rounded-xl" />)}
    </div>
    <Skeleton className="h-64 w-full rounded-xl" />
  </div>
);

const CHUNK_RELOAD_FLAG = 'erp:chunk-reloaded';
const isChunkError = (err) => err?.name === 'ChunkLoadError' || /Loading (CSS )?chunk|Failed to fetch dynamically imported module/i.test(err?.message || '');

/** Recovers from stale chunk URLs after a deploy (one automatic reload), otherwise shows a retry card. */
class ChunkErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error) {
    if (isChunkError(error) && !sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
      sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
      window.location.reload();
    }
  }
  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.location !== this.props.location) this.setState({ error: null });
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-[40vh] flex flex-col items-center justify-center gap-3 text-center" data-testid="page-load-error">
        <p className="text-sm font-medium">This page could not be loaded.</p>
        <p className="text-xs text-muted-foreground">Check your connection and try again.</p>
        <button type="button" onClick={() => { sessionStorage.removeItem(CHUNK_RELOAD_FLAG); window.location.reload(); }}
          className="text-xs font-medium text-primary underline-offset-2 hover:underline" data-testid="page-load-retry">Reload</button>
      </div>
    );
  }
}

/** Suspense + chunk recovery around a routed page (keyed by pathname so errors reset on navigation). */
const LazyPage = ({ children, fallback }) => {
  const location = useLocation();
  return (
    <ChunkErrorBoundary location={location.pathname}>
      <Suspense fallback={fallback}>{children}</Suspense>
    </ChunkErrorBoundary>
  );
};

/** Server unreachable while checking the session: keep the cookies, offer a retry — never bounce to /login. */
const SessionUnavailable = ({ onRetry }) => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-center px-4" data-testid="session-unavailable">
    <p className="text-sm font-medium">We couldn't reach the server to restore your session.</p>
    <p className="text-xs text-muted-foreground">Your sign-in is intact — check your connection and try again.</p>
    <button type="button" onClick={onRetry}
      className="text-xs font-medium text-primary underline-offset-2 hover:underline" data-testid="session-retry-button">Retry</button>
  </div>
);

const Protected = ({ children, shell = true }) => {
  const { user, loading, sessionError, retrySession, mustChangePassword } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <SessionLoading />;
  if (sessionError) return <SessionUnavailable onRetry={retrySession} />;
  if (!user) return <Navigate to="/login" replace />;
  // Credentials issued from the Admin Panel must be replaced before anything else is usable.
  if (mustChangePassword && pathname !== '/change-password') return <Navigate to="/change-password" replace />;
  return shell
    ? <AppShell><LazyPage fallback={<PageSkeleton />}>{children}</LazyPage></AppShell>
    : <LazyPage fallback={<SessionLoading />}>{children}</LazyPage>;
};

const PermGate = ({ module, action = 'view', children }) => {
  const { hasPerm } = useAuth();
  if (!hasPerm(module, action)) return <Navigate to="/" replace />;
  return children;
};

/** Phase 1 feature gate: server-derived ADMIN_PANEL.<feature>.<action> grant, else friendly 403 page. */
const FeatureGate = ({ feature, action = 'view', children }) => {
  const { canFeature } = useAuth();
  if (!canFeature('ADMIN_PANEL', feature, action)) {
    return <Navigate to="/access-denied" replace state={{ reason: `Admin Panel › ${feature} requires '${action}' access.` }} />;
  }
  return children;
};

/** Admin Panel area: protected shell + shared RBAC activation status + feature gate. */
const AdminArea = ({ feature, children }) => (
  <Protected>
    <RbacStatusProvider>
      <FeatureGate feature={feature}>{children}</FeatureGate>
    </RbacStatusProvider>
  </Protected>
);

/** Legacy-route compatibility: redirects preserving nested paths + query string. */
const MovedRoute = ({ to, notice }) => {
  const location = useLocation();
  const params = useParams();
  const splat = params['*'] ? `/${params['*']}` : '';
  useEffect(() => { if (notice) toast.info(notice, { id: `moved-${to}` }); }, [notice, to]);
  return <Navigate to={`${to}${splat}${location.search}`} replace />;
};

const COMING_SOON_KEYS = ['accounting', 'reporting', 'kra', 'training', 'rewards',
  'calendar', 'chats', 'documents', 'games', 'contracts', 'help', 'support', 'client-portal'];

const COMING_SOON_PATHS = {
  'client-portal': '/client-portal',
};

// Old URL → new workspace path (splat + query string preserved by MovedRoute)
const LEGACY_REDIRECTS = [
  ['/leads', '/sales/leads'],
  ['/dashboard', '/sales/dashboard'],
  ['/incentives', '/sales/incentives'],
  ['/projections', '/sales/projections'],
  ['/quotations/*', '/sales/quotations'],
  ['/meetings/*', '/sales/meetings'],
  ['/integrations', '/sales/integrations'],
  ['/settings', '/sales/settings'],
  ['/activity', '/sales/activity'],
  ['/team', '/hrms/employees'],
  ['/org-chart', '/hrms/org-chart'],
  ['/admin/archive', '/recycle-bin'],
  // Phase 1: system-wide pages moved into the Admin Panel (bookmarks preserved)
  ['/admin/permissions', '/admin/roles-permissions', 'Moved to Admin Panel › Roles & Permissions'],
  ['/hrms/permissions', '/admin/roles-permissions', 'Moved to Admin Panel › Roles & Permissions'],
  ['/hrms/masters', '/admin/settings-masters', 'Moved to Admin Panel › Settings & Masters'],
  ['/admin/audit', '/admin/audit-log'],
  // Phase 2 · Task 1: Attendance Centre merged into the single Attendance hub
  ['/hrms/attendance-centre', '/hrms/attendance', 'Attendance Centre is now part of Attendance'],
];

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/change-password" element={<Protected shell={false}><ChangePasswordPage /></Protected>} />
            <Route path="/access-denied" element={<Protected><AccessDeniedPage /></Protected>} />

            {/* Client Portal (separate client authentication — no staff shell) */}
            <Route path="/portal/login" element={<LazyPage fallback={<SessionLoading />}><PortalLoginPage /></LazyPage>} />
            <Route path="/portal" element={<LazyPage fallback={<SessionLoading />}><PortalDashboardPage /></LazyPage>} />
            <Route path="/portal/projects/:id" element={<LazyPage fallback={<SessionLoading />}><PortalProjectPage /></LazyPage>} />

            {/* ERP Launcher (landing) */}
            <Route path="/" element={<Protected><LauncherPage /></Protected>} />

            {/* ---------------- Sales workspace ---------------- */}
            <Route path="/sales" element={<Protected><KanbanPage /></Protected>} />
            <Route path="/sales/leads" element={<Protected><LeadLogPage /></Protected>} />
            <Route path="/sales/dashboard" element={<Protected><DashboardPage /></Protected>} />
            <Route path="/sales/incentives" element={<Protected><IncentivesPage /></Protected>} />
            <Route path="/sales/projections" element={<Protected><ProjectionsPage /></Protected>} />
            <Route path="/sales/quotations" element={<Protected><PermGate module="quotations"><QuotationsPage /></PermGate></Protected>} />
            <Route path="/sales/quotations/new" element={<Protected><PermGate module="quotations" action="create"><QuotationBuilderPage /></PermGate></Protected>} />
            <Route path="/sales/quotations/settings" element={<Protected><PermGate module="quotations" action="manage"><QuotationSettingsPage /></PermGate></Protected>} />
            <Route path="/sales/quotations/:id/edit" element={<Protected><PermGate module="quotations"><QuotationBuilderPage /></PermGate></Protected>} />
            <Route path="/sales/meetings" element={<Protected><PermGate module="meetings"><MeetingsPage /></PermGate></Protected>} />
            <Route path="/sales/meetings/settings" element={<Protected><PermGate module="meetings"><MeetingSettingsPage /></PermGate></Protected>} />
            <Route path="/sales/activity" element={<Protected><PermGate module="activity"><ActivityPage /></PermGate></Protected>} />
            <Route path="/sales/integrations" element={<Protected><PermGate module="integrations"><IntegrationsPage /></PermGate></Protected>} />
            <Route path="/sales/settings" element={<Protected><PermGate module="settings"><SettingsPage /></PermGate></Protected>} />

            {/* ---------------- HRMS workspace ---------------- */}
            <Route path="/hrms" element={<Navigate to="/hrms/employees" replace />} />
            <Route path="/hrms/employees" element={<Protected><PermGate module="team"><EmployeeDirectoryPage /></PermGate></Protected>} />
            <Route path="/hrms/my-profile" element={<Protected><MyProfilePage /></Protected>} />
            <Route path="/hrms/employees/new" element={<Protected><PermGate module="team" action="manage"><AddEmployeeWizardPage /></PermGate></Protected>} />
            <Route path="/hrms/employees/:mid" element={<Protected><PermGate module="team"><EmployeeProfilePage /></PermGate></Protected>} />
            <Route path="/hrms/positions" element={<Protected><PermGate module="masters"><PositionsPage /></PermGate></Protected>} />
            <Route path="/hrms/policy-centre" element={<Protected><PermGate module="masters"><PolicyCentrePage /></PermGate></Protected>} />
            <Route path="/hrms/holiday-calendars" element={<Protected><PermGate module="masters"><HolidayCalendarsPage /></PermGate></Protected>} />
            <Route path="/hrms/recruitment" element={<Protected><PermGate module="team"><RecruitmentPage /></PermGate></Protected>} />
            <Route path="/hrms/induction" element={<Protected><PermGate module="team"><InductionPage /></PermGate></Protected>} />
            <Route path="/hrms/lifecycle" element={<Protected><PermGate module="team"><LifecyclePage /></PermGate></Protected>} />
            <Route path="/hrms/payroll" element={<Protected><PayrollPage /></Protected>} />
            <Route path="/hrms/payroll-setup" element={<Protected><PermGate module="team" action="manage"><PayrollSetupPage /></PermGate></Protected>} />
            <Route path="/hrms/reimbursements" element={<Protected><ReimbursementsPage /></Protected>} />
            <Route path="/hrms/hr-dashboard" element={<Protected><HRDashboardPage /></Protected>} />
            <Route path="/hrms/exits" element={<Protected><ExitManagementPage /></Protected>} />
            <Route path="/hrms/compliance" element={<Protected><ComplianceCalendarPage /></Protected>} />
            <Route path="/hrms/import" element={<Protected><PermGate module="team" action="manage"><EmployeeImportPage /></PermGate></Protected>} />
            <Route path="/hrms/org-chart" element={<Protected><PermGate module="team"><OrgChartPage /></PermGate></Protected>} />
            <Route path="/hrms/leave" element={<Protected><PermGate module="team"><LeavePage /></PermGate></Protected>} />
            <Route path="/hrms/attendance" element={<Protected><PermGate module="team"><AttendancePage /></PermGate></Protected>} />
            <Route path="/hrms/policies" element={<Protected><PermGate module="masters"><PoliciesPage /></PermGate></Protected>} />

            {/* ---------------- Projects workspace ---------------- */}
            <Route path="/projects" element={<Protected><PermGate module="projects"><ProjectsListPage /></PermGate></Protected>} />
            <Route path="/projects/my-tasks" element={<Protected><PermGate module="projects"><MyTasksPage /></PermGate></Protected>} />
            <Route path="/projects/my-calendar" element={<Protected><PermGate module="projects"><MyCalendarPage /></PermGate></Protected>} />
            <Route path="/projects/settings/statuses" element={<Protected><PermGate module="projects"><ProjectStatusSettingsPage /></PermGate></Protected>} />
            <Route path="/projects/settings/templates" element={<Protected><PermGate module="projects"><ProjectTemplatesPage /></PermGate></Protected>} />
            <Route path="/projects/billing-requests" element={<Protected><PermGate module="projects"><BillingRequestsPage /></PermGate></Protected>} />
            <Route path="/projects/:id" element={<Protected><PermGate module="projects"><ProjectDetailPage /></PermGate></Protected>} />

            {/* ---------------- Admin Panel (Phase 1 RBAC; feature-gated) ---------------- */}
            <Route path="/admin" element={<Navigate to="/admin/users" replace />} />
            <Route path="/admin/users" element={<AdminArea feature="userManagement"><AdminUsersPage /></AdminArea>} />
            <Route path="/admin/approvals" element={<AdminArea feature="approvals"><AdminApprovalsPage /></AdminArea>} />
            <Route path="/admin/roles-permissions" element={<AdminArea feature="rolesPermissions"><AdminRolesPermissionsPage /></AdminArea>} />
            <Route path="/admin/departments" element={<AdminArea feature="departments"><AdminDepartmentsPage /></AdminArea>} />
            <Route path="/admin/settings-masters" element={<AdminArea feature="settingsMasters"><MastersPage /></AdminArea>} />
            <Route path="/admin/audit-log" element={<AdminArea feature="auditLog"><AuditLogPage /></AdminArea>} />
            <Route path="/admin/registry" element={<AdminArea feature="moduleRegistry"><AdminRegistryPage /></AdminArea>} />

            {/* ---------------- Common apps ---------------- */}
            <Route path="/announcements" element={<Protected><AnnouncementsPage /></Protected>} />
            <Route path="/recycle-bin" element={<Protected><RecycleBinPage /></Protected>} />
            {COMING_SOON_KEYS.map((key) => (
              <Route key={key} path={COMING_SOON_PATHS[key] || `/${key}`}
                element={<Protected><ComingSoonPage appKey={key} /></Protected>} />
            ))}

            {/* ---------------- Legacy route compatibility ---------------- */}
            {LEGACY_REDIRECTS.map(([from, to, notice]) => (
              <Route key={from} path={from} element={<MovedRoute to={to} notice={notice} />} />
            ))}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
